import { IonContent, IonPage } from "@ionic/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";

import { ActionBar } from "../components/ActionBar";
import { Button, IconButton } from "../components/Button";
import { Chip, ChipRow } from "../components/Chip";
import { FoodPicker } from "../components/FoodPicker";
import { Hero } from "../components/Hero";
import { I } from "../components/icons";
import { Notice } from "../components/Notice";
import { PlanDaySheet } from "../components/PlanDaySheet";
import { NavRow, RowGroup } from "../components/Rows";
import { Screen } from "../components/Shell";
import { Sheet } from "../components/Sheet";
import { SwapSheet } from "../components/SwapSheet";
import { Tag } from "../components/Tag";
import { TopBar } from "../components/TopBar";
import { customFoodByCode, foodTable } from "../domain/customFoods";
import { addDays, dayLabel, todayStr } from "../domain/date";
import { isFastDay as markedFast } from "../domain/dayMarks";
import { planFor, templatesOf, totalsOf } from "../domain/intake";
import { swapFrame } from "../domain/planFit";
import {
  answersFast,
  dayFood,
  generateDay,
  generateWeek,
  planBlocked,
  planTargetsFor,
  type PlanOptions,
} from "../domain/planner";
import { weekView } from "../domain/planView";
import type { FoodEntry, FoodItem, MealSlot } from "../domain/types";
import { foodByCode, loadFoods } from "../lib/foodDb";
import { useDeviceAnswers, useKaya } from "../store/kayaStore";
import {
  addPlanItem,
  applyTemplate,
  clearGenerated,
  regenerateMeal,
  removeTemplate,
  saveTemplate,
  writeGenerated,
} from "../store/toggles";
import "./Food.css";
import "../components/Plan.css";

const n = (v: number) => v.toLocaleString();

type Open =
  | { kind: "day"; ds: string }
  | { kind: "pick"; ds: string }
  | { kind: "swap"; ds: string; id: string }
  | { kind: "apply"; tplId: string }
  | { kind: "delete"; tplId: string }
  | { kind: "new" }
  | null;

/**
 * The week's food, planned (`/food/plan`, ADR-026, ADR-043).
 *
 * Answer first: what the days ahead average against the budget, and what the week is still short
 * of. Then one row per day, tagged by whose it is — generated, yours, a fast day — which opens the
 * day in a sheet, and the saved days ("templates"). "Plan my week" fills every empty meal of the
 * seven days from what the person already eats; "Plan again" clears only what the planner wrote.
 *
 * Fast days: a weekday fast in the answers, or a day marked as a fast on its day sheet (ADR-049),
 * is passed to the planner as `marked`.
 */
export default function FoodPlan() {
  const { S, update, showToast } = useKaya();
  const device = useDeviceAnswers();
  const history = useHistory();
  const today = todayStr();
  const [open, setOpen] = useState<Open>(null);
  const [db, setDb] = useState<FoodItem[] | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);

  useEffect(() => {
    loadFoods()
      .then(setDb)
      .catch((e) => setLoadErr(e instanceof Error ? e.message : "Could not load the food list"));
  }, []);

  const foods = useMemo(() => (db ? foodTable(S, db) : []), [S, db]);
  const lookup = useCallback((code: string) => customFoodByCode(S, code) ?? foodByCode(code), [S]);
  const isFastDay = useCallback((ds: string) => answersFast(S, ds) || markedFast(S, ds), [S]);
  const targetsFor = useCallback((ds: string, fast: boolean) => planTargetsFor(S, ds, fast, device), [S, device]);

  // A future day has no measured work yet, so the budget is the one before any sessions.
  const T = targetsFor(today, false);
  const week = weekView(S, today, T, { lookup, marked: isFastDay });
  const templates = templatesOf(S);
  const tplOf = (id: string) => templates.find((t) => t.id === id) ?? null;
  const blocked = planBlocked(S, T !== null);
  const answered = S.profile.eating?.at !== undefined;

  const planOpts = (): PlanOptions => ({
    foods,
    lookup,
    today,
    targets: targetsFor,
    marked: isFastDay,
    now: Date.now(),
    // A "Plan again" is a new round: the seed moves with the ids the planner has already retired,
    // so two phones in the same state plan the same week, and each round can break ties anew.
    seed: `round:${retired()}`,
  });

  function retired(): number {
    let k = 0;
    for (let i = 0; i < 7; i++) for (const id of Object.keys(S.mealPlan?.days?.[addDays(today, i)]?.off ?? {})) if (id.startsWith("g:")) k++;
    return k;
  }

  const planWeek = (again: boolean) => {
    if (!answered) {
      history.push("/food/questions");
      return;
    }
    if (!db) return;
    const now = Date.now();
    let added = 0;
    update((d) => {
      if (again) for (let i = 0; i < 7; i++) clearGenerated(d, addDays(today, i), undefined, now);
    });
    // Planned from the state as it stands after the clear, then written in one go.
    update((d) => {
      for (const day of generateWeek(d, { ...planOpts(), now })) {
        added += writeGenerated(d, day.ds, day.meals.flatMap((m) => m.items));
      }
    });
    showToast(added ? `Planned ${added} item${added === 1 ? "" : "s"} across the week` : "Every meal already has food — nothing to add");
  };

  const regenerate = (ds: string, meal: MealSlot) => {
    const gone = planFor(S, ds).filter((e) => e.meal === meal && e.id.startsWith("g:"));
    const round = Object.keys(S.mealPlan?.days?.[ds]?.off ?? {}).filter((id) => id.startsWith(`g:${ds}:${meal}:`)).length + 1;
    const day = generateDay(S, ds, planOpts(), { meal, round, avoid: gone.map((e) => e.code || `name:${e.name.toLowerCase()}`) });
    const items = day ? day.meals.flatMap((m) => m.items) : [];
    update((d) => {
      regenerateMeal(d, ds, meal, items);
    });
    showToast(items.length ? `A new ${meal === "snack" ? "snack" : meal}` : "Nothing else fits that meal");
  };

  const apply = (tplId: string, ds: string) => {
    const tpl = tplOf(tplId);
    if (!tpl) return;
    let added = 0;
    update((d) => {
      added = applyTemplate(d, ds, tplId);
    });
    showToast(added ? `${tpl.name} added to ${dayLabel(ds)}` : `${tpl.name} is already in ${dayLabel(ds)}`);
  };

  const swapping = open?.kind === "swap" ? planFor(S, open.ds).find((e) => e.id === open.id) ?? null : null;
  const swapT = open?.kind === "swap" ? targetsFor(open.ds, isFastDay(open.ds)) : null;

  const hero = week.generated ? (
    <Hero
      label={week.heroLabel}
      figure={week.avgKcal}
      unit="kcal a day, on average"
      line={`${T ? `budget ~${n(T.kcal)} est.` : "No budget without your body numbers"} · protein ${week.avgProtein ?? "–"} of ${T?.protein ?? "–"} g`}
      sub={
        <>
          {week.short ? <span className="k-plan-block k-plan-ink">{week.short}</span> : null}
          {week.todayLogged ? <span className="k-plan-block">Today is in your log, so the average starts tomorrow.</span> : null}
        </>
      }
    />
  ) : (
    <Hero
      label="Planned, next 7 days"
      figure={week.avgKcal}
      unit="kcal a day, on average"
      line={`${T ? `budget ~${n(T.kcal)} est.` : "No budget without your body numbers"} · protein ${week.avgProtein ?? "–"} of ${T?.protein ?? "–"} g`}
      sub={
        week.counted
          ? `The average counts the ${week.counted} planned day${week.counted === 1 ? "" : "s"} only. Sessions add to a day’s budget when they happen.`
          : "Nothing planned yet. Plan my week fills it from what you already eat."
      }
    />
  );

  return (
    <IonPage>
      <IonContent>
        <Screen>
          <div className="k-food">
            <TopBar title="Food plan" back={{ to: "/food", label: "Back to Food" }} />
            {hero}

            {blocked ? <Notice tone="caution">{blocked}</Notice> : null}
            {!blocked && week.generated ? (
              <Notice tone="info" icon={I.info}>
                Built from what you already log; nothing you placed is replaced.
              </Notice>
            ) : null}
            {loadErr ? <Notice tone="danger">{loadErr}</Notice> : null}

            <RowGroup heading="Next seven days">
              {week.rows.map((d) => (
                <NavRow
                  key={d.ds}
                  title={d.label}
                  subtitle={d.kcal !== null ? `${n(d.kcal)} kcal · ${d.protein ?? "–"} g protein` : "Not planned"}
                  value={d.tag ? <Tag tone={d.tag} /> : undefined}
                  onClick={() => setOpen({ kind: "day", ds: d.ds })}
                />
              ))}
            </RowGroup>

            <RowGroup heading="Templates">
              {templates.length ? (
                templates.map((tpl) => {
                  const t = totalsOf(tpl.items);
                  return (
                    <NavRow
                      key={tpl.id}
                      kind="action"
                      title={tpl.name}
                      subtitle={`${n(t.kcal)} kcal · ${Math.round(t.p)} g protein · ${t.items} item${t.items === 1 ? "" : "s"}`}
                      action={
                        <span className="k-food-acts">
                          <Button
                            variant="ghost"
                            ariaLabel={`Apply ${tpl.name} to a day`}
                            onClick={() => setOpen({ kind: "apply", tplId: tpl.id })}
                          >
                            Apply to…
                          </Button>
                          <IconButton
                            icon={I.trash}
                            label={`Delete the ${tpl.name} template`}
                            onClick={() => setOpen({ kind: "delete", tplId: tpl.id })}
                          />
                        </span>
                      }
                    />
                  );
                })
              ) : (
                <NavRow
                  kind="value"
                  title="No templates yet"
                  subtitle="A template is a whole day of food with a name. Apply it to any day and every item lands in that day’s plan."
                />
              )}
            </RowGroup>
          </div>

          {blocked ? null : week.generated ? (
            <ActionBar
              secondary={{ label: "Change my answers", onClick: () => history.push("/food/questions") }}
              primary={{ label: "Plan again", icon: I.refresh, onClick: () => planWeek(true), disabled: !db, reason: db ? undefined : "Loading the food list" }}
            />
          ) : (
            <ActionBar
              secondary={week.counted ? { label: "Add a template", onClick: () => setOpen({ kind: "new" }) } : undefined}
              primary={{
                label: answered ? "Plan my week" : "Plan my food",
                icon: I.calendar,
                onClick: () => planWeek(false),
                disabled: answered && !db,
                reason: answered && !db ? "Loading the food list" : undefined,
              }}
            />
          )}
        </Screen>

        {open?.kind === "day" ? (
          <PlanDaySheet
            ds={open.ds}
            today={today}
            T={targetsFor(open.ds, isFastDay(open.ds))}
            fast={isFastDay(open.ds)}
            lookup={lookup}
            canAdd={!!db}
            onClose={() => setOpen(null)}
            onAddFood={() => setOpen({ kind: "pick", ds: open.ds })}
            onSwap={(e) => setOpen({ kind: "swap", ds: open.ds, id: e.id })}
            onRegenerate={(m) => regenerate(open.ds, m)}
          />
        ) : null}

        {open?.kind === "swap" && swapping && swapT ? (
          <SwapSheet
            frame={swapFrame(S, open.ds, swapping, dayFood(S, open.ds, today), swapT, { fast: isFastDay(open.ds) })}
            foods={foods}
            lookup={lookup}
            today={today}
            onClose={() => setOpen({ kind: "day", ds: open.ds })}
          />
        ) : null}

        {open?.kind === "pick" ? (
          <FoodPicker
            title={`Plan food for ${dayLabel(open.ds)}`}
            action="Plan"
            onClose={() => setOpen({ kind: "day", ds: open.ds })}
            onPick={(e: FoodEntry) => {
              const ds = open.ds;
              update((d) => addPlanItem(d, ds, e));
              showToast(`Planned ${e.name}`);
              setOpen({ kind: "day", ds });
            }}
            onPickMany={(es) => {
              const ds = open.ds;
              update((d) => {
                for (const e of es) addPlanItem(d, ds, e);
              });
              showToast(`Planned ${es.length} food${es.length === 1 ? "" : "s"}`);
              setOpen({ kind: "day", ds });
            }}
          />
        ) : null}

        {open?.kind === "apply" ? (
          <ApplySheet
            name={tplOf(open.tplId)?.name ?? "Template"}
            days={week.rows.map((d) => ({ ds: d.ds, label: d.label, kcal: d.kcal }))}
            onClose={() => setOpen(null)}
            onApply={(ds) => {
              apply(open.tplId, ds);
              setOpen(null);
            }}
          />
        ) : null}

        {open?.kind === "delete" ? (
          <DeleteSheet
            name={tplOf(open.tplId)?.name ?? "Template"}
            onClose={() => setOpen(null)}
            onDelete={() => {
              const name = tplOf(open.tplId)?.name ?? "Template";
              update((d) => removeTemplate(d, open.tplId));
              showToast(`Deleted ${name}`);
              setOpen(null);
            }}
          />
        ) : null}

        {open?.kind === "new" ? (
          <NewTemplateSheet
            days={week.rows.filter((d) => planFor(S, d.ds).length).map((d) => ({ ds: d.ds, label: d.label }))}
            onClose={() => setOpen(null)}
            onSave={(ds, name) => {
              const items = planFor(S, ds);
              update((d) => {
                saveTemplate(d, { name, items });
              });
              showToast(`Saved ${name.trim()}`);
              setOpen(null);
            }}
          />
        ) : null}
      </IonContent>
    </IonPage>
  );
}

/** "Apply Office day to…": the seven days, one tap each. */
function ApplySheet({
  name,
  days,
  onClose,
  onApply,
}: {
  name: string;
  days: { ds: string; label: string; kcal: number | null }[];
  onClose: () => void;
  onApply: (ds: string) => void;
}) {
  return (
    <Sheet title={`Apply ${name} to…`} sub="Its items are added to that day's plan" onClose={onClose}>
      <RowGroup>
        {days.map((d) => (
          <NavRow
            key={d.ds}
            title={d.label}
            value={d.kcal !== null ? `${n(d.kcal)} kcal planned` : "Not planned"}
            onClick={() => onApply(d.ds)}
          />
        ))}
      </RowGroup>
    </Sheet>
  );
}

/** Delete behind a confirm: a template is a whole day typed once. */
function DeleteSheet({ name, onClose, onDelete }: { name: string; onClose: () => void; onDelete: () => void }) {
  return (
    <Sheet
      title={`Delete the ${name} template?`}
      sub="Days it was already applied to keep their food."
      onClose={onClose}
      footer={
        <ActionBar
          inSheet
          secondary={{ label: "Cancel", onClick: onClose }}
          primary={{
            label: `Delete the ${name} template`,
            onClick: onDelete,
            icon: I.trash,
            tone: "danger",
          }}
        />
      }
    >
      {null}
    </Sheet>
  );
}

/** A planned day saved under a name. Only days with food can be saved. */
function NewTemplateSheet({
  days,
  onClose,
  onSave,
}: {
  days: { ds: string; label: string }[];
  onClose: () => void;
  onSave: (ds: string, name: string) => void;
}) {
  const [ds, setDs] = useState(days[0]?.ds ?? "");
  const [name, setName] = useState("");
  const ok = ds !== "" && name.trim() !== "";
  return (
    <Sheet
      title="Add a template"
      sub="Save a planned day under a name"
      onClose={onClose}
      footer={
        <ActionBar
          inSheet
          secondary={{ label: "Cancel", onClick: onClose }}
          primary={{
            label: "Save template",
            onClick: () => onSave(ds, name),
            disabled: !ok,
            reason: ok ? undefined : "Name the template first",
          }}
        />
      }
    >
      <span className="k-food-label">Which day</span>
      <ChipRow ariaLabel="Which day">
        {days.map((d) => (
          <Chip key={d.ds} selected={d.ds === ds} onClick={() => setDs(d.ds)}>
            {d.label}
          </Chip>
        ))}
      </ChipRow>
      <label className="k-food-label" htmlFor="k-food-tplname">
        Name
      </label>
      <input
        id="k-food-tplname"
        className="k-food-input"
        value={name}
        placeholder="Office day"
        onChange={(e) => setName(e.target.value)}
      />
    </Sheet>
  );
}
